import React, { useState } from 'react';
import { Box, Container, Typography, Link as MuiLink, TextField, Button, Alert, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

const DeleteData: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    // Open mailto link with pre-filled subject and body
    const subject = encodeURIComponent('Data Deletion Request');
    const body = encodeURIComponent(
      `I would like to request deletion of all my personal data associated with this email address: ${email}\n\n` +
      `I understand that:\n` +
      `- My account and all associated data will be permanently deleted\n` +
      `- This action cannot be undone\n` +
      `- This process may take up to 30 days to complete\n\n` +
      `Please confirm once my data has been deleted.\n\n` +
      `Thank you.`
    );

    window.location.href = `mailto:privacy@snackdapp.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setError('');
  };

  return (
    <Box sx={{ backgroundColor: '#FAF5EA', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="sm">
        <Box sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/" sx={{ color: '#F78764', fontWeight: 600 }}>
            ← Back to Home
          </MuiLink>
        </Box>

        <Paper sx={{ borderRadius: 3, p: { xs: 3, md: 5 }, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#032736', mb: 2 }}>
            Delete Your Data
          </Typography>

          <Typography paragraph sx={{ color: '#666', mb: 3 }}>
            We respect your privacy and your right to control your personal data. Use this form to request
            deletion of all data associated with your Snackd account.
          </Typography>

          <Box sx={{ backgroundColor: '#fff3cd', borderRadius: 2, p: 2, mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#856404' }}>
              <strong>Please note:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>This will permanently delete your account and all associated data</li>
                <li>This includes meal plans, preferences, family member data, and shopping lists</li>
                <li>This action cannot be undone</li>
                <li>Requests are processed within 30 days</li>
              </ul>
            </Typography>
          </Box>

          {submitted ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your email app should have opened with a pre-filled deletion request.
              Please send the email to complete your request. If your email app didn't open,
              please email <strong>privacy@snackdapp.com</strong> directly with your request.
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter the email associated with your account"
                sx={{ mb: 3 }}
                required
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                  },
                }}
              >
                Request Data Deletion
              </Button>
            </form>
          )}

          <Typography variant="body2" sx={{ mt: 3, color: '#999', textAlign: 'center' }}>
            Questions? Contact us at{' '}
            <MuiLink href="mailto:support@snackdapp.com" sx={{ color: '#F78764' }}>
              support@snackdapp.com
            </MuiLink>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default DeleteData;
